import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, CheckCircle2, Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import paymentsService, { type ProviderConfig, type PaymentProvider } from '@/services/payments.service';
import { useTranslation } from 'react-i18next';

const FIELD_META: Record<string, { label: string; type: 'text' | 'password' | 'select'; options?: [string, string][] }> = {
  client_id: { label: 'Client ID', type: 'text' },
  client_secret: { label: 'Client Secret', type: 'password' },
  secret_key: { label: 'Secret Key', type: 'password' },
  username: { label: 'API Username (UID)', type: 'text' },
  password: { label: 'API Passwort', type: 'password' },
  mode: { label: 'Modus', type: 'select', options: [['live', 'Live'], ['sandbox', 'Sandbox (Test)']] },
  region: { label: 'Region', type: 'select', options: [['eu', 'Europa'], ['na', 'Nordamerika'], ['oc', 'Ozeanien']] },
};
const PROVIDER_META: Record<PaymentProvider, { color: string; bg: string; hint: string }> = {
  paypal: { color: '#0070E0', bg: '#e8f1ff', hint: 'REST-App: Client ID + Secret (developer.paypal.com)' },
  stripe: { color: '#635BFF', bg: '#eeecff', hint: 'Secret Key sk_live_… / sk_test_… (Dashboard → API-Schlüssel)' },
  klarna: { color: '#D6699A', bg: '#fdeaf2', hint: 'Username (UID) + Passwort + Region (Merchant Portal)' },
  shopify: { color: '#5E8E3E', bg: '#eaf3e2', hint: 'Nutzt die bestehende Shopify-Integration — keine separaten Zugangsdaten' },
};

function ProviderRow({ pc, open, onToggle }: { pc: ProviderConfig; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const meta = PROVIDER_META[pc.provider];
  const [form, setForm] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [unlocked, setUnlocked] = useState<Set<string>>(new Set()); // autofill engelleme (readOnly → focus'ta açılır)
  const connected = pc.last_test_status === 'success';

  useEffect(() => {
    const init: Record<string, string> = {};
    for (const f of pc.fields) {
      const v = pc.config?.[f];
      init[f] = v === '[HIDDEN]' ? '' : (v || (FIELD_META[f]?.type === 'select' ? FIELD_META[f]!.options![0][0] : ''));
    }
    setForm(init); setDirty(false);
  }, [pc]);

  const saveM = useMutation({
    mutationFn: () => paymentsService.saveConfig(pc.provider, form),
    onSuccess: () => { toast.success(`${pc.label} gespeichert`); setDirty(false); qc.invalidateQueries({ queryKey: ['payment-providers'] }); },
    onError: () => toast.error(t('common:state.saveFailed', 'Speichern fehlgeschlagen')),
  });
  const testM = useMutation({
    mutationFn: () => paymentsService.test(pc.provider),
    onSuccess: (r) => { r.success ? toast.success(`${pc.label} verbunden ✓`) : toast.error(`Test fehlgeschlagen: ${r.message || ''}`); qc.invalidateQueries({ queryKey: ['payment-providers'] }); },
    onError: () => toast.error(t('common:state.testFailed', 'Test fehlgeschlagen')),
  });
  const set = (f: string, v: string) => { setForm((p) => ({ ...p, [f]: v })); setDirty(true); };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      {/* Header — clickable */}
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-black" style={{ backgroundColor: meta.bg, color: meta.color }}>
          {pc.label[0]}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800">{pc.label}</div>
          <div className="truncate text-xs text-slate-400">{connected ? 'Verbunden' : meta.hint}</div>
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> {'Aktiv'}</span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">{t('common:x3.notConnected', 'Nicht verbunden')}</span>
        )}
        <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-slate-100 px-4 py-4">
          {pc.viaShopIntegration ? (
            <div className="flex items-center gap-2">
              <p className="flex-1 text-xs text-slate-500">Shopify Payments nutzt die bestehende <span className="font-medium text-slate-700">Shopify-Integration</span> (Einstellungen → Integrationen). Keine separaten Zugangsdaten nötig. Status &amp; Rückerstattungen laufen über die Shopify Admin API.</p>
              <Button variant="outline" onClick={() => testM.mutate()} disabled={testM.isPending} className="h-9 shrink-0 rounded-lg">
                {testM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verbindung testen'}
              </Button>
            </div>
          ) : (
          <>
          <div className="grid gap-3 sm:grid-cols-2">
            {pc.fields.map((f) => {
              const fm = FIELD_META[f] || { label: f, type: 'text' as const };
              return (
                <div key={f} className={fm.type === 'select' ? 'sm:col-span-1' : ''}>
                  <Label className="text-xs font-medium text-slate-500">{fm.label}</Label>
                  {fm.type === 'select' ? (
                    <Select value={form[f] || fm.options![0][0]} onValueChange={(v) => set(f, v)}>
                      <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{fm.options!.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={fm.type === 'password' ? 'password' : 'text'}
                      value={form[f] || ''}
                      onChange={(e) => set(f, e.target.value)}
                      placeholder={pc.config?.[f] === '[HIDDEN]' ? '•••••••• gespeichert' : ''}
                      className="mt-1 h-9 font-mono text-xs"
                      autoComplete="new-password"
                      name={`vd_${pc.provider}_${f}`}
                      spellCheck={false}
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      readOnly={!unlocked.has(f)}
                      onFocus={() => setUnlocked((s) => (s.has(f) ? s : new Set(s).add(f)))}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={() => saveM.mutate()} disabled={saveM.isPending || !dirty} className="h-9 rounded-lg bg-[#0f172a] px-4 text-sm font-semibold text-white hover:bg-slate-800">
              {saveM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
            </Button>
            <Button variant="outline" onClick={() => testM.mutate()} disabled={testM.isPending || !pc.connected} className="h-9 rounded-lg">
              {testM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verbindung testen'}
            </Button>
            {pc.last_test_status === 'failed' && pc.last_test_error && <span className="ml-1 truncate text-xs text-rose-600">{pc.last_test_error}</span>}
          </div>
          </>
          )}
        </div>
      )}
    </div>
  );
}

const PaymentsSection: React.FC = () => {
  const { t } = useTranslation();
  const { data: providers = [], isLoading } = useQuery({ queryKey: ['payment-providers'], queryFn: () => paymentsService.getProviders() });
  const [openProvider, setOpenProvider] = useState<string | null>(null);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <CreditCard className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-800">{t('common:x3.paymentProviders', 'Zahlungsanbieter')}</h2>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">Verbinde PayPal, Stripe oder Klarna, um Zahlungsstatus zu sehen und Rückerstattungen durchzuführen.</p>
        </div>
      </div>
      {isLoading ? (
        <div className="flex h-24 items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="space-y-2.5">
          {providers.map((pc) => (
            <ProviderRow key={pc.provider} pc={pc} open={openProvider === pc.provider} onToggle={() => setOpenProvider((cur) => (cur === pc.provider ? null : pc.provider))} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentsSection;
